<script setup lang="ts">
import { vMaska } from 'maska'

interface InputProps {
  name: string
  type:
    | 'date'
    | 'email'
    | 'hidden'
    | 'number'
    | 'password'
    | 'search'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'textarea'
  label?: string
  modelValue?: unknown
  value?: unknown
  variant?: 'primary' | 'secondary' | 'success' | 'error' | 'neutral'
  orientation?: 'vertical' | 'horizontal'
  labelClasses?: unknown
  hideLabel?: boolean
  warning?: string
  step?: number
  minValue?: number
  absoluteErrorPlacement?: boolean
  moneyMask?: boolean
  calendarFormat?: boolean
  labelSpan?: string
  isAmountProposal?: boolean
}

const props = withDefaults(defineProps<InputProps>(), {
  variant: 'primary',
  orientation: 'vertical',
  step: 1,
  absoluteErrorPlacement: false,
})

const attrs = useAttrs()
const id = useId()
const currentVariant = ref(props.variant)

const {
  value: vValue,
  errorMessage,
  handleBlur,
  handleChange,
} = useField(toRef(props, 'name'), undefined, {
  syncVModel: true,
})

const isDisabled = computed(
  () => typeof attrs.disabled === 'string' || !!attrs.disabled,
)

watch(errorMessage, (newValue) => {
  currentVariant.value = newValue ? 'error' : props.variant
})

onMounted(() => {
  if (!vValue.value) {
    handleChange(props.modelValue ?? props.value, false)
  }
})

function increment() {
  let stepValue = props.step * 100 || 100
  let tempVal = vValue.value
    ? ((vValue.value as number) * 100 + stepValue) / 100
    : stepValue / 100

  vValue.value = Number(tempVal.toFixed(2))
}

function decrement() {
  let stepValue = props.step * 100 || 100
  let tempVal = vValue.value
    ? ((vValue.value as number) * 100 - stepValue) / 100
    : -(stepValue / 100)

  if (props.minValue && tempVal < props.minValue) {
    vValue.value = props.minValue
    return
  }

  vValue.value = Number(tempVal.toFixed(2))
}

defineOptions({
  inheritAttrs: false,
})

const phoneNumberCompleted = ref(false)

const vMaskaOptions = {
  onMaska: (detail: { completed: any }) => {
    detail.completed
      ? (phoneNumberCompleted.value = true)
      : (phoneNumberCompleted.value = false)
  },
}

const moneyMaska = {
  preProcess: (val: string) => val.replace(/[^0-9.]/g, ''),
  postProcess: (val: string) => {
    if (!val) return ''

    const sub = 3 - (val.includes('.') ? val.length - val.indexOf('.') : 0)

    return Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    })
      .format(Number(val))
      .slice(0, sub ? -sub : undefined)
  },
}

const parseMoneyValue = (raw: string) => {
  
  let v = (raw ?? '').toString().replace(/[^0-9.]/g, '')

  const parts = v.split('.')
  if (parts.length > 2) v = `${parts[0]}.${parts.slice(1).join('')}`

  let [intPart, decPart]= v.split('.')
  intPart = (intPart || '').slice(0, 7)
  if (decPart !== undefined) decPart = decPart.slice(0, 2)

  return { intPart, decPart, hasDot: v.includes('.') }
}

const handleMoneyBlur = (e: FocusEvent) => {
  if (props.isAmountProposal && props.moneyMask && props.name === "amount_float" ){
    const input = e.target as HTMLInputElement
    const { intPart, decPart } = parseMoneyValue(input.value)
    
    const grouped = Number(intPart || '0').toLocaleString('en-US')
    const cents = (decPart ?? '').padEnd(2, '0').slice(0, 2)

    const formatted = `$${grouped}.${cents}`

    vValue.value = formatted
    input.value = formatted
  }
  handleBlur(e)
}


const limitInput = (event: Event) => {
  const input = event.target as HTMLInputElement

  const { intPart, decPart, hasDot } = parseMoneyValue(input.value)
    if (!intPart && (decPart === undefined || decPart === '')) {
    vValue.value = '$0'
    input.value = '$0'
    return
  }

  const grouped = intPart ? Number(intPart).toLocaleString('en-US') : '0'
  const formatted = hasDot ? `$${grouped}.${decPart ?? ''}` : intPart ? `$${grouped}` : ''

  vValue.value = formatted
  input.value = formatted
}

watch(
  () => vValue.value,
  (newValue) => {
    if (!props.isAmountProposal && !props.moneyMask && props.name !== "amount_float") return
    if (typeof newValue !== 'string') return

    const { intPart, decPart, hasDot } = parseMoneyValue(newValue)
    const next =
      !intPart && !hasDot
        ? ''
        : hasDot
          ? `$${Number(intPart || '0').toLocaleString('en-US')}.${decPart ?? ''}`
          : `$${Number(intPart).toLocaleString('en-US')}`

    if (next !== newValue) vValue.value = next
  }
)
</script>

<template>
  <div
    :class="[
      'relative flex w-full gap-1',
      {
        'flex-col': orientation === 'vertical',
        'items-center': orientation === 'horizontal',
        'pt-5': errorMessage,
      },
    ]"
  >
    <label
      v-if="label"
      :for="id"
      :class="[
        'text-xs uppercase text-[#8B90A5]',
        {
          'sr-only': props.hideLabel,
          '-mt-5': errorMessage,
        },
        props.labelClasses,
      ]"
    >
      {{ props.label }}
      <span v-if="props.labelSpan" class="lowercase">
        {{ props.labelSpan }}
      </span>
    </label>

    <div class="w-full">
      <div
        :class="[
          'relative flex rounded-md',
          { grow: orientation === 'horizontal' },
        ]"
      >
        <slot
          name="prepend"
          v-bind="{
            classes:
              'inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500',
          }"
        />
        <textarea
          v-if="props.type === 'textarea'"
          :id="id"
          v-model="vValue"
          rows="3"
          :class="[
            'block w-full min-w-0 rounded-md p-2 resize-none',
            {
              'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500':
                currentVariant === 'error',
            },
          ]"
          v-bind="$attrs"
          @blur="handleBlur"
        />

        <input
          v-else-if="props.type != 'tel' && !props.moneyMask"
          :id="id"
          v-model="vValue"
          :type="props.type"
          :class="[
            'block w-full min-w-0 flex-1 rounded-md p-2',
            //variants
            {
              'focus:border-primary-500 focus:ring-primary-500':
                currentVariant === 'primary',
              'focus:border-gray-500 focus:ring-gray-500':
                currentVariant === 'secondary',
              'focus:border-emerald-500 focus:ring-emerald-500':
                currentVariant === 'success',
              'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500':
                currentVariant === 'error',
              'border-brand-neutral bg-brand-neutral text-slate-600 focus:border-[#A8C8F8] focus:ring-[#A8C8F8]':
                currentVariant === 'neutral',
              'disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500':
                $attrs.disabled !== false,

              'rounded-none rounded-r-md': !!$slots.prepend,
              'rounded-none rounded-l-md': !!$slots.append,
            },
          ]"
          :aria-invalid="!!errorMessage"
          :aria-describedby="`${id}-error`"
          v-bind="$attrs"
          autocomplete="off"
          @blur="handleBlur"
        />

        <input
          v-else-if="props.moneyMask && props.type != 'tel'"
          :id="id"
          v-model="vValue"
          v-maska:[moneyMaska]
          type="text"
          :class="[
            'block w-full min-w-0 flex-1 rounded-md p-2',
            //variants
            {
              'focus:border-primary-500 focus:ring-primary-500':
                currentVariant === 'primary',
              'focus:border-gray-500 focus:ring-gray-500':
                currentVariant === 'secondary',
              'focus:border-emerald-500 focus:ring-emerald-500':
                currentVariant === 'success',
              'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500':
                currentVariant === 'error',
              'border-brand-neutral bg-brand-neutral text-slate-600 focus:border-[#A8C8F8] focus:ring-[#A8C8F8]':
                currentVariant === 'neutral',
              'disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500':
                $attrs.disabled !== false,

              'rounded-none rounded-r-md': !!$slots.prepend,
              'rounded-none rounded-l-md': !!$slots.append,
            },
          ]"
          :aria-invalid="!!errorMessage"
          :aria-describedby="`${id}-error`"
          v-bind="$attrs"
          autocomplete="off"
          @input="limitInput"
          @blur="handleMoneyBlur"
        />

        <input
          v-else-if="props.type === 'tel'"
          :id="id"
          v-model="vValue"
          v-maska:[vMaskaOptions]
          type="text"
          data-maska="(###) ### ####"
          :class="[
            'block w-full min-w-0 flex-1 rounded-md p-2',
            //variants
            {
              'focus:border-primary-500 focus:ring-primary-500':
                currentVariant === 'primary',
              'focus:border-gray-500 focus:ring-gray-500':
                currentVariant === 'secondary',
              'focus:border-emerald-500 focus:ring-emerald-500':
                currentVariant === 'success',
              'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500':
                currentVariant === 'error',
              'border-brand-neutral bg-brand-neutral text-slate-600 focus:border-[#A8C8F8] focus:ring-[#A8C8F8]':
                currentVariant === 'neutral',
              'disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500':
                $attrs.disabled !== false,

              'rounded-none rounded-r-md': !!$slots.prepend,
              'rounded-none rounded-l-md': !!$slots.append,
            },
          ]"
          :aria-invalid="!!errorMessage"
          :aria-describedby="`${id}-error`"
          v-bind="$attrs"
          autocomplete="off"
          @blur="handleBlur"
        />

        <slot
          name="append"
          v-bind="{
            classes:
              'inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-gray-500',
          }"
        />

        <div
          v-if="props.type === 'number'"
          class="absolute right-0 flex h-full flex-col content-center items-center justify-center gap-1 pr-1"
        >
          <button
            type="button"
            :disabled="isDisabled"
            :class="[
              'rounded px-0.5 focus:outline-none focus:ring-2',
              {
                'focus:ring-primary-600': props.variant === 'primary',
                'focus:ring-gray-500': currentVariant === 'secondary',
                'focus:ring-emerald-500': currentVariant === 'success',
                'focus:ring-red-500': currentVariant === 'error',
                'cursor-not-allowed': isDisabled,
              },
            ]"
            @click="increment"
          >
            <HeroiconsChevronUp
              class="h-3 w-3 text-gray-400"
              aria-hidden="true"
            />
          </button>

          <button
            type="button"
            :disabled="isDisabled"
            :class="[
              'rounded px-0.5 focus:outline-none focus:ring-2',
              {
                'focus:ring-primary-600': props.variant === 'primary',
                'focus:ring-gray-500': currentVariant === 'secondary',
                'focus:ring-emerald-500': currentVariant === 'success',
                'focus:ring-red-500': currentVariant === 'error',
                'cursor-not-allowed': isDisabled,
              },
            ]"
            @click="decrement"
          >
            <HeroiconsChevronDown
              class="h-3 w-3 text-gray-400"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <p
        v-show="errorMessage"
        :class="[
          'z-10 mb-3 rounded p-1 text-xs font-normal text-red-700',
          {
            'absolute bg-white': props.absoluteErrorPlacement,
          },
        ]"
      >
        {{ errorMessage }}
      </p>

      <p
        v-show="warning"
        :class="[
          'z-10 mt-1 rounded bg-white p-1 text-xs text-red-700',
          {
            absolute: props.absoluteErrorPlacement,
          },
        ]"
      >
        {{ warning }}
      </p>
    </div>
  </div>
</template>
